import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import MetaTag from "../layout/MetaTag";
import swal from "sweetalert";
import {
  productAction,
  clearErrorAction,
} from "../redux/actions/productAction";
import { Helmet } from "react-helmet";
import { createSearchedKeywordAction } from "../redux/actions/searchAction";

import "./sports.css";

import SearchIcon from "@mui/icons-material/Search";

import heroShoeImage from "../images/shoe.png";
import SportsPagePagination from "./SportsPagePagination";

function SportsShoesCard() {
  const dispatch = useDispatch();
  const { error, loading, products } = useSelector((state) => state.products);

  const [searchInput, setSearchInput] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [priceInput, setPriceInput] = useState([0, 50000]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let isMounted = true;
    axios.get('/api/categories')
      .then(res => {
        if (isMounted && res.data && res.data.categories) {
          setCategories(res.data.categories);
        }
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  const [filters, setFilters] = useState({
    keyword: "",
    category: "",
    tag: "",
    price: [0, 50000],
  });

  const productList = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products]
  );
  const resultPerPages = 16;

  useEffect(() => {
    dispatch(
      productAction(
        filters.keyword,
        filters.category,
        filters.tag,
        filters.price
      )
    );
  }, [dispatch, filters]);

  useEffect(() => {
    if (filters.keyword) {
      dispatch(createSearchedKeywordAction(filters.keyword));
    }
  }, [dispatch, filters.keyword]);

  useEffect(() => {
    if (error) {
      swal({
        title: "Validation",
        text: typeof error === "string" ? error : "Unable to load products.",
        icon: "error",
      });
      dispatch(clearErrorAction());
    }
  }, [dispatch, error]);

  const priceInputHandler = (index, value) => {
    const nextPrice = [...priceInput];
    nextPrice[index] = Number(value) || 0;
    setPriceInput(nextPrice);
  };

  const submitSearchHandler = (e) => {
    e.preventDefault();
    setFilters((prev) => ({
      ...prev,
      keyword: searchInput.trim(),
    }));
    setMobileFiltersOpen(false);
  };

  const applyFiltersHandler = (e) => {
    e.preventDefault();
    setFilters({
      keyword: searchInput.trim(),
      category: categoryInput,
      tag: tagInput,
      price: priceInput,
    });
    setMobileFiltersOpen(false);
  };

  const resetFiltersHandler = () => {
    setSearchInput("");
    setCategoryInput("");
    setTagInput("");
    setPriceInput([0, 50000]);
    setFilters({
      keyword: "",
      category: "",
      tag: "",
      price: [0, 50000],
    });
    setMobileFiltersOpen(false);
  };

  const activeFilterCount = [
    filters.keyword,
    filters.category,
    filters.tag,
    filters.price[0] > 0 || filters.price[1] < 50000 ? "price" : "",
  ].filter(Boolean).length;

  return (
    <Fragment>
      <MetaTag
        title="Shop Sneakers and Shoes Online | IM Online Shop"
        description={`Find ${
          filters.keyword || "premium"
        } sneakers, sports shoes, loafers and boots at IM Online Shop. Filter by category, style and price to choose your next pair.`}
        keywords={`${
          filters.keyword || "sneakers"
        }, shoes online, sports shoes, loafers, boots, IM Online Shop`}
        canonical="https://imonline.shop/Product"
        image="/output-onlinepngtools.png"
      />

      <Helmet>
        <script type="application/ld+json">{`
          [
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "IM Online Shop",
              "url": "https://imonline.shop/",
              "logo": "https://imonline.shop/output-onlinepngtools.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-XXX-XXXXXXX",
                "contactType": "Customer Service"
              },
              "sameAs": []
            },
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "IM Online Shop",
              "url": "https://imonline.shop/",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://imonline.shop/find/shoes?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imonline.shop/"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "Sneakers and Shoes",
                "item": "https://imonline.shop/Product"
              }]
            },
            {
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Sneakers and Shoes",
              "url": "https://imonline.shop/Product",
              "description": "Shop sneakers, sports shoes, loafers and boots online at IM Online Shop.",
              "mainEntity": {
                "@type": "ItemList",
                "itemListElement": ${JSON.stringify(
                  productList.slice(0, 12).map((product, index) => {
                    const productUrl = `https://imonline.shop/product/${
                      product.slug || product._id
                    }`;
                    const imageUrl =
                      product?.mainImage?.[0]?.imageurl ||
                      product?.image?.[0]?.imageurl ||
                      "";
                    return {
                      "@type": "ListItem",
                      position: index + 1,
                      item: {
                        "@type": "Product",
                        url: productUrl,
                        name: product.name,
                        image: imageUrl,
                        description: product.discription,
                      },
                    };
                  })
                )}
              }
            }
          ]
        `}</script>
      </Helmet>

      <section className="sneakers-page-hero">
        <div className="sneakers-hero-copy">
          <span>Sneaker Collection</span>
          <h1>Find your next pair</h1>
          <p>
            Browse latest drops, daily wear, formal shoes, loafers, boots, and
            premium styles in one place.
          </p>
          <div className="sneakers-hero-actions">
            <a href="#sneakers-products">Shop collection</a>
            <button type="button" onClick={resetFiltersHandler}>
              Reset filters
            </button>
          </div>
          <div className="sneakers-hero-stats">
            <div>
              <strong>{productList.length}</strong>
              <small>Live products</small>
            </div>
            <div>
              <strong>COD</strong>
              <small>Available</small>
            </div>
            <div>
              <strong>Easy</strong>
              <small>Returns</small>
            </div>
          </div>
        </div>
        <div className="sneakers-hero-visual" aria-hidden="true">
          <img src={heroShoeImage} alt="Stylish sneaker on display" />
          <div>
            <span>New season picks</span>
            <strong>Premium streetwear</strong>
          </div>
        </div>
      </section>

      <section className="sneakers-shop-shell">
        <aside className={`sneakers-filter-panel ${mobileFiltersOpen ? "mobile-open" : ""}`}>
          <div className="sneakers-filter-heading">
            <h2>Filter Products</h2>
            <div className="sneakers-filter-heading-btns">
              <button type="button" onClick={resetFiltersHandler}>
                Reset
              </button>
              <button 
                type="button" 
                className="sneakers-mobile-filter-close-btn"
                onClick={() => setMobileFiltersOpen(false)}
              >
                Close
              </button>
            </div>
          </div>

          <form onSubmit={submitSearchHandler} className="sneakers-search-form">
            <label htmlFor="sneaker-search">Search</label>
            <div className="search-div-main-container-in-shoes-section">
              <input
                id="sneaker-search"
                className="search-input"
                placeholder="Type shoe name..."
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="search-btn-input"
                type="submit"
                aria-label="Search shoes"
              >
                <SearchIcon />
              </button>
            </div>
          </form>

          <form onSubmit={applyFiltersHandler} className="sneakers-filter-form">
            <div className="filter-box">
              <div className="price-filter-div">
                <div className="filter-heading">Price Limit</div>
                <div className="sneakers-price-input-row">
                  <input
                    aria-label="Minimum price"
                    min="0"
                    type="number"
                    value={priceInput[0]}
                    onChange={(e) => priceInputHandler(0, e.target.value)}
                  />
                  <input
                    aria-label="Maximum price"
                    min="0"
                    type="number"
                    value={priceInput[1]}
                    onChange={(e) => priceInputHandler(1, e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="filter-select-box-div">
              <label htmlFor="sneaker-category">Category</label>
              <select
                id="sneaker-category"
                onChange={(e) => setCategoryInput(e.target.value)}
                value={categoryInput}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat._id || cat.name} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-sort-by-product-category-div">
              <label htmlFor="sneaker-sort">Sort by Collection</label>
              <select
                id="sneaker-sort"
                onChange={(e) => setTagInput(e.target.value)}
                value={tagInput}
              >
                <option value="">All Collections</option>
                <option value="New Arrival">New Arrival</option>
                <option value="On Sale">On Sale</option>
                <option value="Best Seller">Best Seller</option>
                <option value="Trending">Trending</option>
                <option value="Featured">Featured</option>
                <option value="New Collection">New Collection</option>
              </select>
            </div>

            <div className="fliter-submit-button-main-container">
              <button type="submit" className="product-filter-button">
                Apply Filters
              </button>
            </div>
          </form>
        </aside>

        <main className="sneakers-products-panel" id="sneakers-products">
          <div className="sneakers-results-bar">
            <div>
              <h2>All Sneakers</h2>
              <p>
                {productList.length} products found · {activeFilterCount} active
                filters
              </p>
            </div>
            <div className="sneakers-mobile-filter-bar">
              <button 
                type="button" 
                className="sneakers-mobile-filter-toggle"
                onClick={() => setMobileFiltersOpen(true)}
              >
                Filter & Sort
              </button>
            </div>
            <div className="sneakers-results-badges">
              <span>Latest drops</span>
              <span>Secure checkout</span>
            </div>
          </div>

          <SportsPagePagination
            products={productList}
            resultPerPages={resultPerPages}
            loading={loading}
          />
        </main>
      </section>

      <section className="sneakers-confidence-banner">
        <div>
          <span>Premium picks</span>
          <h2>Fresh pairs, safer checkout, faster decisions.</h2>
          <p>
            Save your favourites, compare recent views, and reserve selected
            shoes with advance payment before stock moves out.
          </p>
        </div>
        <div className="sneakers-confidence-points">
          <article>
            <strong>New drops</strong>
            <small>Updated collections</small>
          </article>
          <article>
            <strong>Secure pay</strong>
            <small>Verified checkout</small>
          </article>
          <article>
            <strong>Fast support</strong>
            <small>Order help available</small>
          </article>
        </div>
      </section>
    </Fragment>
  );
}

export default SportsShoesCard;
